/**
 * HoProjectDetail - Router component for homeowner project detail pages.
 * Routes to the appropriate detail component based on project state.
 */

import * as React from "react";
import { useNavigate } from "react-router-dom";
import projectPlaceholder from "@/assets/images/project-placeholder.svg";
import { SkeletonRows } from "@/components/ui/skeleton";
import type {
	AvatarTone,
	HoProject,
	StageTone,
} from "../../data/mock-homeowner";
import { useProject } from "../../hooks/use-projects";
import {
	getDesignerById,
	type Project,
} from "../../services/projects-api.service";
import { ProjectViewingAs } from "../project-viewing-as";
import { Btn, Eyebrow, hoStyles } from "./design-primitives";
import { HoActiveProjectDetail } from "./ho-active-project-detail";

interface HoProjectDetailProps {
	projectId: string;
}

const AVATAR_TONES: AvatarTone[] = ["ink", "amber", "stone", "cognac", "terra"];

function getRandomAvatarTone(): AvatarTone {
	return AVATAR_TONES[Math.floor(Math.random() * AVATAR_TONES.length)];
}

function getStageInfo(status: Project["status"]): {
	stage: string;
	stageTone: StageTone;
	pct: number;
} {
	switch (status) {
		case "completed":
			return { stage: "Completed", stageTone: "done", pct: 100 };
		case "in_progress":
			return { stage: "In Progress", stageTone: "active", pct: 50 };
		case "seeking_designer":
			return { stage: "Seeking designer", stageTone: "awaiting", pct: 0 };
		case "awaiting_funding":
			return { stage: "Awaiting funding", stageTone: "risk", pct: 0 };
		case "draft":
			return { stage: "Draft", stageTone: "neutral", pct: 0 };
		case "cancelled":
			return { stage: "Cancelled", stageTone: "risk", pct: 0 };
		default:
			return { stage: "Unknown", stageTone: "neutral", pct: 0 };
	}
}

async function transformProject(apiProject: Project): Promise<HoProject> {
	const stageInfo = getStageInfo(apiProject.status);
	const createdAt = new Date(apiProject.createdAt);
	const now = new Date();
	const daysSinceCreated = Math.floor(
		(now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24),
	);
	const totalDays = (apiProject.timelineWeeks || 12) * 7;

	let designerName: string | null = null;
	let designerIni: string | null = null;
	let designerTone: AvatarTone | null = null;
	let studio: string | null = null;

	// Use enriched designer data from API if available
	let designerStyle: string | null = null;
	if (apiProject.designer) {
		designerName = apiProject.designer.name;
		designerIni = apiProject.designer.name
			.split(" ")
			.map((n) => n[0])
			.join("")
			.toUpperCase()
			.slice(0, 2);
		designerTone = getRandomAvatarTone();
		studio = apiProject.designer.firmName || null;
		designerStyle = apiProject.designer.styles?.[0] || null;
	} else if (apiProject.designerId) {
		// Fallback: fetch designer if not enriched
		try {
			const designer = await getDesignerById(apiProject.designerId);
			if (designer) {
				designerName = designer.name;
				designerIni = designer.name
					.split(" ")
					.map((n) => n[0])
					.join("")
					.toUpperCase()
					.slice(0, 2);
				designerTone = getRandomAvatarTone();
				studio = designer.firmName || null;
			}
		} catch {
			// Designer fetch failed, leave as null
		}
	}

	// Build config from property rooms (BHK) and sizeSqft
	const propertyRooms = apiProject.property?.rooms;
	const sizeSqft = apiProject.property?.sizeSqft;
	let configStr = "";
	if (propertyRooms?.bedrooms) {
		configStr = `${propertyRooms.bedrooms}BHK`;
		if (sizeSqft) {
			configStr += ` · ${sizeSqft}sqft`;
		} else if (propertyRooms.bathrooms) {
			configStr += ` · ${propertyRooms.bathrooms} Bath`;
		}
	} else if (apiProject.scope === "full_home") {
		configStr = "Full home";
	} else {
		configStr = "Partial";
	}

	// Use designer style if available, otherwise fallback
	const styleStr =
		designerStyle ||
		apiProject.scopeDetails?.rooms?.[0] ||
		(apiProject.scope === "full_home" ? "Full home" : "Partial");

	const isShared =
		apiProject.status === "in_progress" || !!apiProject.designerId;

	// Build location from property data
	const loc = apiProject.property?.locality
		? apiProject.property.city
			? `${apiProject.property.locality}, ${apiProject.property.city}`
			: apiProject.property.locality
		: apiProject.property?.city || "Location TBD";

	return {
		id: apiProject.id,
		name: apiProject.title,
		loc,
		img:
			apiProject.projectImages?.[0] ||
			apiProject.beforeImages?.[0] ||
			apiProject.afterImages?.[0] ||
			projectPlaceholder,
		stage: stageInfo.stage,
		stageTone: stageInfo.stageTone,
		config: configStr,
		style: styleStr,
		designer: designerName,
		studio: studio,
		designerIni: designerIni,
		designerTone: designerTone,
		pct: stageInfo.pct,
		day: daysSinceCreated,
		days: totalDays,
		budget: apiProject.budgetMax || apiProject.budgetMin || 0,
		spent: 0,
		escrow: apiProject.totalFunded || 0,
		shared: isShared,
	};
}

function NotFoundState({ onBack }: { onBack: () => void }) {
	return (
		<div style={hoStyles.page}>
			<div style={{ maxWidth: 640, margin: "0 auto", padding: "80px 48px" }}>
				<Eyebrow
					color="var(--color-dashboard-muted)"
					style={{ fontSize: 10, letterSpacing: "0.18em" }}
				>
					404 · NOT FOUND
				</Eyebrow>
				<h1
					style={{
						fontFamily: "var(--font-display)",
						fontWeight: 500,
						fontSize: 40,
						color: "var(--color-dashboard-text)",
						marginTop: 16,
						lineHeight: 1.1,
						letterSpacing: "-0.02em",
					}}
				>
					Project not found
				</h1>
				<p
					style={{
						fontFamily: "var(--font-body)",
						fontSize: 15,
						color: "var(--color-dashboard-muted)",
						marginTop: 12,
						lineHeight: 1.6,
					}}
				>
					This project doesn't exist or you don't have access to it.
				</p>
				<div style={{ marginTop: 32 }}>
					<Btn variant="primary" onClick={onBack}>
						Back to My Projects
					</Btn>
				</div>
			</div>
		</div>
	);
}

function LoadingState() {
	return (
		<div style={hoStyles.page}>
			<div
				style={{
					maxWidth: 640,
					margin: "0 auto",
					padding: "80px 48px",
					textAlign: "center",
				}}
			>
				<SkeletonRows
					label="Loading project..."
					rows={4}
					rowClassName="h-16 rounded-xl"
				/>
			</div>
		</div>
	);
}

export function HoProjectDetail({ projectId }: HoProjectDetailProps) {
	const navigate = useNavigate();

	const { data: apiProject, isLoading, isError } = useProject(projectId);

	const [transformedProject, setTransformedProject] =
		React.useState<HoProject | null>(null);
	const [isTransforming, setIsTransforming] = React.useState(false);

	React.useEffect(() => {
		if (apiProject) {
			setIsTransforming(true);
			transformProject(apiProject).then((project) => {
				setTransformedProject(project);
				setIsTransforming(false);
			});
		} else {
			setTransformedProject(null);
		}
	}, [apiProject]);

	const handleBack = () => {
		navigate("/projects");
	};

	if (isError || (!isLoading && !apiProject)) {
		return <NotFoundState onBack={handleBack} />;
	}

	// This page is the homeowner's view. A project the user is on as its
	// designer or contractor shows a switch to that role instead.
	if (apiProject?.viewerRole && apiProject.viewerRole !== "homeowner") {
		return (
			<ProjectViewingAs
				title={apiProject.title}
				status={apiProject.status}
				viewerRole={apiProject.viewerRole}
				onBack={handleBack}
			/>
		);
	}

	if (isLoading || isTransforming || !transformedProject) {
		return <LoadingState />;
	}

	const detailProps = {
		project: transformedProject,
		onBack: handleBack,
	};

	return <HoActiveProjectDetail {...detailProps} />;
}
